import { useState, useCallback } from 'react';
import previewReportService from '../services/previewReportService';

/**
 * Custom hook for managing preview report PDFs
 * Provides functions to fetch, open, download, and manage preview reports
 */
export const usePreviewReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Reset state
  const resetState = useCallback(() => {
    setError(null);
    setPdfData(null);
    setPdfUrl(null);
  }, []);

  // Fetch preview report from API
  const fetchPreviewReport = useCallback(async (appId, fileType = 'PreviewReport') => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`🔄 Fetching preview report for appId: ${appId}`);
      
      const result = await previewReportService.previewreportfile(appId, fileType);
      
      if (result.success && result.data) {
        const reportUrl = result.data.path || result.data.url;
        
        if (reportUrl) {
          // Check if it's a full URL or needs to be constructed
          let fullUrl = reportUrl;
          if (!reportUrl.startsWith('http')) {
            // Construct full URL for S3 or relative paths
            if (reportUrl.includes('amazonaws.com')) {
              fullUrl = reportUrl.startsWith('//') ? `https:${reportUrl}` : 
                        reportUrl.startsWith('/') ? `https:${reportUrl}` : `https://${reportUrl}`;
            } else {
              // For S3 bucket paths without domain
              fullUrl = `https://nabl7t.s3.ap-south-1.amazonaws.com/${reportUrl}`;
            }
          }
          
          setPdfUrl(fullUrl);
          console.log(`✅ Preview report URL set: ${fullUrl}`);
          return { success: true, url: fullUrl, data: result.data };
        } else {
          throw new Error('Preview report found but no valid URL available');
        }
      } else if (result.notFound) {
        setError('No preview report available. Please generate one first.');
        return { success: false, notFound: true, message: 'Preview report not found' };
      } else {
        throw new Error(result.message || 'Failed to fetch preview report');
      }
    } catch (err) {
      const errorMessage = err.message || 'Error accessing preview report';
      setError(errorMessage);
      console.error('❌ Error fetching preview report:', err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchForm131Report = useCallback(async (appId) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`🔄 Fetching fetchForm131Report for appId: ${appId}`);
      
      const result = await previewReportService.form131File(appId);
      
      if (result.success && result.data) {
        console.log('Form131Data',result.data)
         return { success: true, data: result.data };
        } else {
          throw new Error('Form131Data found but no valid URL available');
        }
    } catch (err) {
      const errorMessage = err.message || 'Error accessing Form131Data';
      setError(errorMessage);
      console.error('❌ Error fetching Form131Data:', err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Generate new preview report
  const generatePreviewReport = useCallback(async (orgId, appId, labTypeId = '1', includeObservations = false) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`🔄 Generating preview report for appId: ${appId}`);
      
      const result = await previewReportService.generateAndSavePreviewReport(
        orgId, appId, labTypeId, 'PreviewReport', includeObservations
      );
      
      if (result.success && result.path) {
        // Construct full URL for the generated report
        let fullUrl = result.path;
        if (!result.path.startsWith('http')) {
          fullUrl = `https://nabl7t.s3.ap-south-1.amazonaws.com/${result.path}`;
        }
        
        setPdfUrl(fullUrl);
        console.log(`✅ Preview report generated and URL set: ${fullUrl}`);
        return { success: true, url: fullUrl, data: result };
      } else {
        throw new Error(result.message || 'Failed to generate preview report');
      }
    } catch (err) {
      const errorMessage = err.message || 'Error generating preview report';
      setError(errorMessage);
      console.error('❌ Error generating preview report:', err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Download PDF directly
  const downloadPDF = useCallback(async (url, filename = null) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch PDF');
      
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || `PreviewReport_${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      
      return { success: true };
    } catch (err) {
      console.error('❌ Error downloading PDF:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Open PDF in new tab
  const openInNewTab = useCallback((url) => {
    if (url) {
      window.open(url, '_blank');
      return { success: true };
    }
    return { success: false, error: 'No URL provided' };
  }, []);

  // Fetch PDF as blob for embedded viewing
  const fetchPDFAsBlob = useCallback(async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch PDF');
      
      const blob = await response.blob();
      setPdfData(blob);
      return { success: true, blob };
    } catch (err) {
      console.error('❌ Error fetching PDF as blob:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  return {
    // State
    loading,
    error,
    pdfUrl,
    pdfData,
    
    // Actions
    fetchPreviewReport,
    fetchForm131Report,
    generatePreviewReport,
    downloadPDF,
    openInNewTab,
    fetchPDFAsBlob,
    resetState,
    
    // Utilities
    setError,
    setLoading
  };
};

export default usePreviewReport;